import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import { FORM_KEYS } from '@/api/useForm';

import type { AxiosError } from 'axios';
import type { Form, CreateFormPayload, UploadFileResponse } from '@/types/form';

export function useCreateForm() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: CreateFormPayload) => {
            const { data } = await api.post<Form>(API_ROUTES.forms, payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: FORM_KEYS.all });
        },
        onError: (error: AxiosError<Record<string, string[]>>) => {
            const data = error.response?.data;
            if (error.response?.status === 400 && data) {
                const firstError = Object.values(data).flat()[0];
                if (firstError) {
                    toast.error(firstError);
                    return;
                }
            }
            toast.error('No se pudo crear el formulario. Intenta de nuevo.');
        },
    });
}

export function useUpdateForm(formSlug: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: Partial<CreateFormPayload>) => {
            const { data } = await api.patch<Form>(API_ROUTES.formDetail(formSlug), payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: FORM_KEYS.detail(formSlug) });
            queryClient.invalidateQueries({ queryKey: FORM_KEYS.all });
            toast.success('Formulario actualizado');
        },
        onError: () => {
            toast.error('No se pudo actualizar el formulario.');
        },
    });
}

export function useDeleteForm() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (formSlug: string) => {
            await api.delete(API_ROUTES.formDetail(formSlug));
            return formSlug;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: FORM_KEYS.all });
            toast.success('Formulario eliminado');
        },
        onError: () => {
            toast.error('No se pudo eliminar el formulario.');
        },
    });
}

export function useDuplicateForm() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (formSlug: string) => {
            const { data } = await api.post<Form>(API_ROUTES.formDuplicate(formSlug));
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: FORM_KEYS.all });
            toast.success('Formulario duplicado!');
        },
        onError: () => {
            toast.error('Algo salio mal. Intenta de nuevo.');
        },
    });
}

export function useUploadFile(formSlug: string) {
    return useMutation({
        mutationFn: async ({ file, fieldId }: { file: File; fieldId: number }) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('field_id', String(fieldId));
            const { data } = await api.post<UploadFileResponse>(
                API_ROUTES.formUploadFile(formSlug),
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            return data;
        },
        onError: () => {
            toast.error('No se pudo subir el archivo');
        },
    });
}
